import React from 'react';
import FeedCard from '../shared/FeedCard';
import RegularCard from '../shared/RegularCard';
import { DiscoverCollectionCard, DiscoverBoardCard } from '../shared/DiscoverCardWrappers';
import { detectContentType, CONTENT_TYPES } from '../../utils/normalizeCardData';

// Strip cards render at w-[80vw] max-w-72 (288px desktop) — request images
// sized for that, not the full-width feed's defaults (which made retina
// devices pull 1200w srcset candidates into 288px slots). Module-level
// constant so FeedCard's memo() stays effective.
export const STRIP_IMAGE_SIZING = { surface: 'medium', sizes: '(max-width: 640px) 80vw, 288px' };

export const STRIP_SLOT_CLASS = 'flex-shrink-0 w-[80vw] max-w-72 snap-start';

/**
 * One feed item as the card Home shows for it — the same pattern as
 * SocialFeed.js: collections and boards get their preview wrappers, a
 * regular (a show on a schedule at a place) its own card, everything else
 * goes through autoNormalizeCardData → UnifiedCard (FeedCard).
 *
 * Shared by the strip (FeedSection) and the grids the Home refresh added
 * (Travel inspiration, New places), so the two can't disagree about what a
 * card is. `slotClassName` is the wrapper's layout class.
 */
export function renderFeedCard(item, index, { buildActions, modals: modalsProp, imageSizing = STRIP_IMAGE_SIZING, slotClassName = STRIP_SLOT_CLASS, cardContext }) {
  const type = detectContentType(item);
  const modals = modalsProp || {};
  const overflow = {
    onLike: modals.handleLike,
    onComment: modals.handleOpenComments,
    onSave: modals.handleSave,
    onShare: item.is_own_content ? modals.handleShare : undefined,
    onNotInterested: !item.is_own_content ? modals.handleNotInterested : undefined,
    onReport: !item.is_own_content ? modals.handleReport : undefined,
    onBlockUser: !item.is_own_content && (item.user_id || item.creator_id) ? modals.handleBlockUser : undefined
  };

  if (type === CONTENT_TYPES.COLLECTION) {
    return (
      <div key={`collection-${item.id || item.content_id || index}`} className={slotClassName}>
        <DiscoverCollectionCard collection={item} imageSizing={imageSizing} {...overflow} />
      </div>
    );
  }

  // A regular mixed into an Events strip: not a feed item, so no like/comment/dismiss actions.
  if (item.content_type === 'regular') {
    return (
      <div key={`regular-${item.id}`} className={slotClassName}>
        <RegularCard regular={item} />
      </div>
    );
  }

  if (type === CONTENT_TYPES.BOARD) {
    return (
      <div key={`board-${item.id || item.content_id || index}`} className={slotClassName}>
        <DiscoverBoardCard board={item} imageSizing={imageSizing} {...overflow} />
      </div>
    );
  }

  const showCreator = !item._isRecommendation && !item._isArtistEvent && !item._isEventGroup
    && item.content_type !== 'event' && item.content_type !== 'artist_event' && item.content_type !== 'event_group';

  return (
    <div key={`${item.content_type || 'item'}-${item.id || item.content_id || index}`} className={slotClassName}>
      <FeedCard
        item={item}
        buildActions={buildActions}
        showCreator={showCreator}
        imageSizing={imageSizing}
        cardContext={cardContext}
      />
    </div>
  );
}
